<template>
  <div class="hr-page">
    <Hero :title="data?.hero?.title" :description="data?.hero?.description">
      <template #image>
        <ElementsImage v-if="data?.hero?.image" v-bind="data.hero.image" loading="eager" />
      </template>
    </Hero>

    <ElementsContainer>
      <form class="row g-4 md:gx-5 md:gy-6 flex flex-wrap -mx-2 gap-y-4" @submit.prevent="onSubmit">
        <div v-if="form.error" class="col-12 w-full px-2">
          <div class="bg-red-100 text-red-700 p-4 rounded">{{ form.error }}</div>
        </div>
        <div v-if="form.success" class="col-12 w-full px-2">
          <div class="bg-green-100 text-green-700 p-4 rounded">
            {{ $t('humanResources.successMessage') }}
          </div>
        </div>

        <div class="col-12 w-full px-2">
          <fieldset class="row g-4 md:gx-5 md:gy-6 flex flex-wrap -mx-2 gap-y-4" :disabled="form.status">
            <div class="sm:col-6 mr-1/2 w-full sm:w-1/2 px-2">
              <select v-model="formFields.position" required name="position" class="w-full h-12 border px-4 rounded outline-none focus:border-black">
                <option value="" disabled selected>{{ $t('humanResources.selectPosition') }}</option>
                <option v-for="opt in fields.position?.attributes?.options || []" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div class="sm:col-6 w-full sm:w-1/2 px-2">
              <ElementsFormInput v-model="formFields.name" required name="name" v-bind="fields.name" :error="formErrors.name" />
            </div>
            <div class="sm:col-6 w-full sm:w-1/2 px-2">
              <ElementsFormInput v-model="formFields.surname" required name="surname" v-bind="fields.surname" :error="formErrors.surname" />
            </div>
            <div class="sm:col-6 w-full sm:w-1/2 px-2">
              <ElementsFormInput v-model="formFields.email" required name="email" type="email" v-bind="fields.email" :error="formErrors.email" />
            </div>
            <div class="sm:col-6 w-full sm:w-1/2 px-2">
              <ElementsFormInput
                v-model="formFields.phone_number"
                required
                name="phone_number"
                type="tel"
                minlength="10"
                maxlength="20"
                v-bind="fields.phone_number"
                :error="formErrors.phone_number"
              />
            </div>
            <div class="col-12 w-full px-2">
              <textarea
                v-model="formFields.about"
                required
                name="about"
                rows="7"
                class="w-full border p-4 rounded outline-none focus:border-black resize-none"
                :placeholder="fields.about?.placeholder || $t('humanResources.aboutPlaceholder')"
              ></textarea>
            </div>
            <div class="col-12 w-full px-2">
              <ElementsFormInput v-model="formFields.resume" type="file" name="resume" v-bind="fields.resume" :error="formErrors.resume" />
            </div>
            <div class="col-fill flex items-center w-full px-2">
              <ElementsFormCheckbox v-model="formFields.terms" required name="terms" :error="formErrors.terms">
                <span v-html="fields.terms?.label || $t('common.accept')"></span>
              </ElementsFormCheckbox>
            </div>
            <div class="col-auto w-full px-2">
              <ElementsButton :disabled="form.status" tag="button" type="submit">
                {{ fields.submit?.label || $t('common.send') }}
              </ElementsButton>
            </div>
          </fieldset>
        </div>
      </form>
    </ElementsContainer>
  </div>
</template>

<script setup lang="ts">
  import fetchApi from "~/utils/fetchApi"

  const { data: responseData } = await useAsyncData("hr", () =>
    fetchApi<any>("human-resources-page", {
      wrappedByKey: "data",
      populate: {
        hero: { populate: "*" },
        form: { populate: "*" }
      }
    })
  )

  const data = computed(() => responseData.value || {})

  const fields = computed(() => {
    if (!data.value?.form) return {}
    return data.value.form.reduce((acc: any, curr: any) => {
      const { name, ...rest } = curr
      acc[name] = rest
      return acc
    }, {})
  })

  const formFields = reactive({
    position: "",
    name: "",
    surname: "",
    email: "",
    phone_number: "",
    about: "",
    resume: null as File | null,
    terms: false
  })

  const formErrors = reactive<Record<string, string>>({})
  const form = reactive({
    status: false,
    error: "",
    success: false
  })

  const onSubmit = async () => {
    form.status = true
    form.error = ""
    form.success = false

    const formData = new FormData()
    Object.entries(formFields).forEach(([key, value]) => {
      if (value !== null) {
        formData.append(key, value as any)
      }
    })
    formData.append("recaptcha", "token-placeholder")

    try {
      const response = await fetch("/api/human-resources", {
        method: "POST",
        body: formData
      })

      if (response.ok) {
        form.success = true
        formFields.position = ""
        formFields.name = ""
        formFields.surname = ""
        formFields.email = ""
        formFields.phone_number = ""
        formFields.about = ""
        formFields.resume = null
        formFields.terms = false
      } else {
        const result = await response.json()
        form.error = result.message || "Bir hata oluştu."
        if (result.data) {
          Object.assign(formErrors, result.data)
        }
      }
    } catch (e: any) {
      form.error = e.message || "Bağlantı hatası"
    } finally {
      form.status = false
    }
  }

  const { t } = useI18n()

  useSeoMeta({
    title: () => data.value?.hero?.title || t("menu.humanResources"),
    description: () => data.value?.hero?.description || ""
  })
</script>
